import React, { useEffect, useRef } from 'react';
import FeedbackMessageBubble from './FeedbackMessageBubble';
import type { FeedbackMessage, FeedbackRating } from '../../types/feedbackChat';

/**
 * The scrolling message list.
 *
 * Auto-scroll follows the same idiom as MessagesPage's ConversationThread, with
 * one addition: it does NOT yank the view down if the member has scrolled up to
 * re-read something. Being pulled away mid-sentence while a reply lands is a
 * small thing that feels broken.
 */

interface Props {
  messages: FeedbackMessage[];
  sending: boolean;
  onRate: (messageId: string, rating: FeedbackRating) => void;
  /**
   * Height of the scroll region. The page passes a viewport fraction; the
   * pop-up passes `100%` because its own flex column already owns the height —
   * a vh value inside a fixed-height panel produces a second scrollbar.
   */
  maxHeight?: string;
}

export default function FeedbackThread({ messages, sending, onRate, maxHeight = '52vh' }: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const nearBottom = distanceFromBottom < 120;

    if (nearBottom) {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length, sending]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-4 overflow-y-auto py-2 flex-1"
      style={{ maxHeight }}
      aria-live="polite"
      aria-atomic="false"
    >
      {messages.map((message) => (
        <FeedbackMessageBubble key={message.id} message={message} onRate={onRate} />
      ))}

      {sending && <TypingIndicator />}

      <div ref={endRef} />
    </div>
  );
}

/**
 * Stands in for streaming. Replies are non-streaming (firebase-functions is
 * pinned below the version that supports callable streaming), and at one to
 * three sentences the wait is short enough that a typing indicator covers it.
 */
function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div
        className="rounded-3xl rounded-bl-lg px-4 py-3 flex items-center gap-1.5"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid var(--melio-border)' }}
        aria-label="Melio Assistant is writing a reply"
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="rounded-full"
            style={{
              width: 6,
              height: 6,
              backgroundColor: 'var(--melio-olive)',
              animation: 'melioFeedbackPulse 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
        <style>{`
          @keyframes melioFeedbackPulse {
            0%, 60%, 100% { opacity: 0.25; }
            30% { opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            [aria-label="Melio Assistant is writing a reply"] span {
              animation: none !important;
              opacity: 0.5 !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
